/**
 * Resident wildlife (see WILDLIFE, wildlife_plan.md): each band (a herd of
 * grazers, a flock of gas bags, a school of skyfish) belongs to a home rock,
 * is made from that rock's seed (`bandsOf`), and is remembered by its area
 * only once something about it changes (members killed, come back).
 *
 * A band that's awake and calm plays a **script**: keys the server wrote
 * ahead by running the band's behaviour, which everyone plays back at the
 * same clock (the snapshot's `time`, as drifting rocks are placed). So the
 * client never decides where an animal is: it reads the keys (`trackAt`).
 */
import { SKYFISH, WILDLIFE } from "../constants";
import { v2, type Vec2 } from "../math";
import { Rng } from "./rng";
import type { AsteroidDef, Biome } from "./types";

export type BandKind = "grazer" | "gasbag" | "skyfish";

/** A band as its rock's seed makes it. */
export interface BandDef {
  /** "<rock>:<kind><n>": the same every time the rock is made. */
  id: string;
  kind: BandKind;
  /** Its home: grazers walk it, schools circle it, gas bags keep to trees within WILDLIFE.range of its middle. */
  rock: string;
  /** How many members it has, full (a school: how many fish). */
  size: number;
  /** What makes each band its own: where it starts on its rock, its looks (each member's id is hashed for its variant). */
  seed: number;
  /** A school: which way it goes round (1 anticlockwise, -1 clockwise) and how far from the rock's middle. */
  school?: { turn: number; out: number };
}

/** What its area remembers about a band that isn't as its seed made it. */
export interface BandRecord {
  /** Which members are alive (bit i, member i; a school: fish i). */
  alive: number;
  /** When (ms) it last lost a member or got one back: one comes back every WILDLIFE.refill after. */
  changedAt: number;
}

/** Every member alive. */
export const fullBand = (size: number): number => (size >= 31 ? 0x7fffffff : (1 << size) - 1);

/** How many of a band's members are alive. */
export function aliveCount(alive: number): number {
  let n = 0;
  for (let a = alive; a; a &= a - 1) n++;
  return n;
}

/** Member `i` of band `band`: its creature id. */
export const memberId = (band: string, i: number): string => `${band}:${i}`;

/** The band and member a creature id is of (null for anything else: a whale, an incomer). */
export function memberOf(id: string): { band: string; i: number } | null {
  const k = id.lastIndexOf(":");
  if (k < 0) return null;
  const i = Number(id.slice(k + 1));
  return Number.isInteger(i) ? { band: id.slice(0, k), i } : null;
}

/** Where grass or moss grows: grazers live there (on rocks that pull). */
const GRAZING: ReadonlySet<Biome> = new Set(["meadow", "grove", "wreck", "twilight"]);
/** Green enough for skyfish (and the water balls, which they swim in). */
export const FISHY: ReadonlySet<Biome> = new Set(["meadow", "grove", "living", "wreck", "lichen", "water"]);

/**
 * The bands rock `def` is home to, from its seed, kind and biome (no grid
 * needed: whether the rock can hold them now is worked out when it's built).
 * Grazers only on big rocks (see isBigRock), with grass or moss: they
 * walk, so they need room. Gas bags where trees grow. Skyfish round green
 * rocks and inside water balls.
 */
export function bandsOf(def: AsteroidDef): BandDef[] {
  const rng = new Rng((def.seed ^ 0x2545f491) >>> 0);
  const out: BandDef[] = [];
  const add = (kind: BandKind, size: number, school?: BandDef["school"]) =>
    out.push({ id: `${def.id}:${kind[0]}${out.length}`, kind, rock: def.id, size, seed: rng.int(1, 0x7fffffff), ...(school ? { school } : {}) });
  const biome = def.biome;
  if (!biome || def.dead || def.pirateBase) return out;

  if (def.big && GRAZING.has(biome)) {
    const herds =
      def.kind === "home" ? 6
      : biome === "meadow" ? Math.min(12, Math.max(1, Math.round(def.radius / 20)))
      : biome === "grove" ? (rng.next() < 0.6 ? 1 : 0)
      : rng.next() < 0.4 ? 1 : 0;
    for (let k = 0; k < herds; k++) add("grazer", rng.int(WILDLIFE.herd[0], WILDLIFE.herd[1]));
  }

  if (def.kind !== "water") {
    const ranges =
      def.kind === "home" ? 3
      : biome === "grove" || biome === "living" ? 1 + Math.floor(def.radius / 45)
      : biome === "meadow" ? (rng.next() < 0.5 ? 1 : 0)
      : 0;
    for (let k = 0; k < ranges; k++) add("gasbag", rng.int(WILDLIFE.flock[0], WILDLIFE.flock[1]));
  }

  if (FISHY.has(biome) && def.radius >= 6) {
    const schools = def.kind === "water" ? rng.int(1, 2) : biome === "lichen" ? (rng.next() < 0.5 ? 1 : 0) : rng.next() < 0.85 ? 1 : 0;
    for (let k = 0; k < schools; k++) {
      // Two in a water ball swim at different depths, so they don't run through each other.
      const out = def.kind === "water" ? def.radius * (k === 0 ? 0.55 : 0.32) : def.radius + rng.range(SKYFISH.out[0], SKYFISH.out[1]) + k * 6;
      add("skyfish", rng.int(SKYFISH.fish[0], SKYFISH.fish[1]), { turn: rng.next() < 0.5 ? -1 : 1, out: Math.round(out * 10) / 10 });
    }
  }
  return out;
}

// ------------------------------------------------------------ scripts

/** One moment of one member's script: where it is from its rock's grid origin, which way is up (or round, for a school), and what it's doing. */
export interface ScriptKey {
  /** Seconds after the script's `from`. */
  t: number;
  at: Vec2;
  /** A grazer: how far round its rock it stands (its up); a gas bag: its heading; a school: its `round`. */
  a: number;
  /** A grazer: which way it faces along the ground (1 or -1). */
  f: number;
  /** Eating: a grazer head down, a gas bag at a crown. */
  e: boolean;
  /** Which of the script's `rocks` `at` is from (a gas bag crossing to another rock's trees). */
  r: number;
}

/** One member's keys, in order of `t`. */
export interface Track {
  m: number;
  keys: ScriptKey[];
}

/** What a band will do from `from` (s, on the snapshot clock) to `to`. */
export interface Script {
  from: number;
  to: number;
  /** Rocks the keys are from (`ScriptKey.r`); the home rock first. */
  rocks: string[];
  tracks: Track[];
}

/** A calm band as it's sent: its script, and what's needed to draw it. */
export interface BandState {
  id: string;
  kind: BandKind;
  seed: number;
  alive: number;
  size: number;
  /** A school: which way round and how far out (the rest is `alive`, `seed`, and `round` from the keys). */
  turn?: number;
  out?: number;
  from: number;
  rocks: string[];
  tracks: Track[];
}

/** Where a member is at `t` (s after its script's `from`): between the keys either side, held at the ends. */
export function trackAt(keys: readonly ScriptKey[], t: number): ScriptKey {
  const n = keys.length;
  if (!n) return { t, at: v2(), a: 0, f: 1, e: false, r: 0 };
  if (t <= keys[0]!.t) return keys[0]!;
  if (t >= keys[n - 1]!.t) return keys[n - 1]!;
  // The last key at or before t (keys at the same t: the later one, so a rock change takes).
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (keys[mid]!.t <= t) lo = mid;
    else hi = mid;
  }
  const p = keys[lo]!;
  const q = keys[hi]!;
  if (q.r !== p.r || q.t <= p.t) return { ...p, t };
  const k = (t - p.t) / (q.t - p.t);
  return { t, at: v2(p.at.x + (q.at.x - p.at.x) * k, p.at.y + (q.at.y - p.at.y) * k), a: lerpAngle(p.a, q.a, k), f: p.f, e: p.e, r: p.r };
}

/** Between two angles the short way round (a school's `round` isn't wrapped, so it's plain). */
function lerpAngle(a: number, b: number, k: number): number {
  let d = b - a;
  if (Math.abs(d) > Math.PI) d = Math.atan2(Math.sin(d), Math.cos(d));
  return a + d * k;
}

/** A key as the wire carries it (see SNAPSHOT bands), so the server plays back what everyone else does. */
export function quantiseKey(k: ScriptKey): ScriptKey {
  return {
    t: Math.round(k.t * 20) / 20,
    at: v2(Math.round(k.at.x * 32) / 32, Math.round(k.at.y * 32) / 32),
    a: Math.round(k.a * 1000) / 1000,
    f: k.f < 0 ? -1 : 1,
    e: k.e,
    r: k.r,
  };
}
